import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import { requestLoginCode, verifyLoginCode } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Mail, ShieldCheck, Loader2, ArrowLeft } from 'lucide-react';
import GoogleAuthButton from './GoogleAuthButton';

// Passwordless login: email -> 6-digit code -> signed in.
// Used standalone (page) and inside a Dialog (header modal).
export function PasswordlessLoginForm({ onSuccess }) {
  const { login } = useAuth();
  const { t } = useLang();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const sendCode = async () => {
    if (!email.trim()) { toast.error(t('expEmail')); return; }
    setBusy(true);
    try {
      await requestLoginCode({ email: email.trim() });
      setSent(true);
      toast.success(t('expCodeSent'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.trim().length !== 6) { toast.error(t('expEnterCode')); return; }
    setBusy(true);
    try {
      const res = await verifyLoginCode({ email: email.trim(), code: code.trim() });
      login(res.data.token, res.data.user);
      toast.success(t('expWelcomeBack'));
      onSuccess?.(res.data.user);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code incorrect');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="passwordless-login-form">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mx-auto mb-3">
          <Mail className="w-6 h-6 text-gold-dark" />
        </div>
        <h2 className="font-display text-xl font-bold text-rolex-green">{t('expSignInTitle')}</h2>
        <p className="text-sm text-rolex-green/65 mt-1">{t('expSignInDesc')}</p>
      </div>

      {!sent ? (
        <div className="space-y-3">
          <GoogleAuthButton />
          <div className="relative text-center">
            <span className="bg-white px-3 text-[11px] uppercase tracking-widest text-rolex-green/45 relative z-10">{t('orDivider')}</span>
            <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-rolex-green font-semibold text-sm">{t('expEmail')}</Label>
            <Input type="email" autoComplete="email" placeholder="votre@email.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendCode()}
              className="rounded-xl h-12" data-testid="passwordless-email-input" />
          </div>
          <Button onClick={sendCode} disabled={busy} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold" data-testid="passwordless-send-code">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('expSendCode')}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <button type="button" onClick={() => { setSent(false); setCode(''); }} className="flex items-center gap-1 text-sm text-rolex-green/65 hover:text-rolex-green">
            <ArrowLeft className="w-3.5 h-3.5" /> {email}
          </button>
          <Label className="text-rolex-green font-semibold text-sm">{t('expEnterCode')}</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => e.key === 'Enter' && verify()}
            inputMode="numeric" maxLength={6} placeholder="••••••"
            className="rounded-xl text-center font-mono text-2xl tracking-[0.4em] h-14" data-testid="passwordless-code-input" />
          <Button onClick={verify} disabled={busy} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold" data-testid="passwordless-verify-code">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('expVerify')}
          </Button>
          <button type="button" onClick={sendCode} className="w-full text-xs text-rolex-green/55 hover:text-rolex-green underline-offset-2 hover:underline">
            {t('expResendCode')}
          </button>
        </div>
      )}

      <div className="rounded-xl bg-gold/10 border border-gold/30 px-3 py-2.5 flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 text-gold-dark shrink-0 mt-0.5" />
        <p className="text-xs text-rolex-green/80 leading-snug">{t('expNoPasswordHint')}</p>
      </div>
    </div>
  );
}

export function PasswordlessLoginModal({ open, onOpenChange, onSuccess }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-border sm:max-w-md p-6" data-testid="passwordless-login-modal">
        <DialogHeader className="sr-only">
          <DialogTitle>Connexion</DialogTitle>
          <DialogDescription>Connexion sans mot de passe par code email</DialogDescription>
        </DialogHeader>
        <PasswordlessLoginForm onSuccess={(u) => { onSuccess?.(u); onOpenChange(false); }} />
      </DialogContent>
    </Dialog>
  );
}
